// Location: frontend/src/Routes/AdminRoute.jsx
import React, { useEffect, useState } from 'react'
import { Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { MdDashboard, MdPermContactCalendar } from 'react-icons/md';
import { FaFileAlt, FaUsers } from 'react-icons/fa';
import { IoMdSettings } from 'react-icons/io';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../Utilities/ContextAPI/AuthProvider';
import { RiMenuAddFill, RiTeamFill } from 'react-icons/ri';
import { TbListDetails } from 'react-icons/tb';
import Profile from '../admin/Component/Profile';
import { HiMenuAlt2 } from "react-icons/hi";

const AdminRoute = (props) => {
    const { Component } = props;
    const { user, loading, checkLogin, logOut } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Redirect if not authenticated and not loading
    useEffect(() => {
        if (!loading && !user) {
            navigate("/login");
        }
    }, [loading, user, navigate]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-light-bg">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    const currentUser = user;

    const handleLogOut = () => {
        logOut();
    };

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    const NavItem = ({ to, icon, label }) => (
        <li>
            <NavLink
                to={to}
                className={({ isActive }) =>
                    `flex items-center px-6 py-3.5 mb-2 rounded-xl transition-all duration-200 group ${isActive
                        ? 'bg-primary text-white shadow-lg shadow-primary/30 font-semibold'
                        : 'text-gray-500 hover:bg-white hover:text-primary hover:shadow-md'
                    }`
                }
                onClick={() => setSidebarOpen(false)} // Close sidebar on mobile on click
            >
                <span className="text-xl mr-3 group-hover:scale-110 transition-transform duration-200">{icon}</span>
                <span className="font-medium tracking-wide">{label}</span>
            </NavLink>
        </li>
    );

    return (
        <div className="flex h-screen bg-light-bg overflow-hidden font-sans">

            {/* Overlay for mobile sidebar */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-30 lg:hidden backdrop-blur-sm transition-opacity"
                    onClick={() => setSidebarOpen(false)}
                ></div>
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:static top-0 left-0 z-40 h-full w-72 bg-[#f8f9fa] shadow-2xl transition-transform duration-300 ease-in-out lg:translate-x-0
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="h-full flex flex-col">
                    {/* Sidebar Header/Logo */}
                    <div className="flex items-center justify-center h-24 border-b border-gray-100 bg-white">
                        <Link to={'/dashboard'} className='flex items-center gap-2 transition-opacity hover:opacity-80'>
                            <img src="/infinexLogo.svg" alt="Infinex Logo" className="w-40" />
                        </Link>
                    </div>

                    {/* Navigation Links */}
                    <div className="flex-1 overflow-y-auto py-6 px-4 custom-scrollbar">
                        <h6 className="px-6 mb-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Main Menu</h6>
                        <ul className="space-y-1">
                            <NavItem to="/dashboard" icon={<MdDashboard />} label="Dashboard" />

                            {currentUser?.role === 'admin' && (
                                <NavItem to="/ManageTeam" icon={<FaUsers />} label="Team Manage" />
                            )}
                        </ul>

                        <h6 className="px-6 mt-8 mb-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Content</h6>
                        <ul className="space-y-1">
                            <NavItem to="/ManageContact" icon={<MdPermContactCalendar />} label="Contact Us" />
                            <NavItem to="/ManagePortfolio" icon={<TbListDetails />} label="Portfolio" />
                            <NavItem to="/ManageCareer" icon={<FaFileAlt />} label="Career" />
                            <NavItem to="/aboutadmin" icon={<MdDashboard />} label="About" />
                            <NavItem to="/ServicesAdmin" icon={<IoMdSettings />} label="Services" />
                            <NavItem to="/ManageBlogs" icon={<IoMdSettings />} label="Blogs" />
                            <NavItem to="/TestimonialAdmin" icon={<MdDashboard />} label="Testimonials" />
                            <NavItem to="/setting" icon={<IoMdSettings />} label="Settings" />
                        </ul>
                    </div>

                    {/* Sidebar Footer */}
                    <div className="p-4 bg-gray-50 border-t border-gray-100">
                        <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                                {currentUser.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{currentUser.name}</p>
                                <p className="text-xs text-gray-500 truncate">{currentUser.role}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content Wrapper */}
            <div className="flex-1 flex flex-col h-full overflow-hidden w-full relative">

                {/* Navbar */}
                <header className="bg-[#f8f9fa] lg:bg-transparent px-4 lg:px-8 py-4 flex items-center justify-between shrink-0">
                    <button
                        onClick={toggleSidebar}
                        className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-white hover:shadow-md transition-all text-2xl"
                    >
                        <HiMenuAlt2 />
                    </button>

                    {/* Desktop Header Title (Optional, can be breadcrumbs) */}
                    <div className="hidden lg:block">
                        {/* <h2 className="text-xl font-bold text-gray-800">Overview</h2> */}
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Profile Dropdown using Bootstrap for now, or could replace with custom Tailwind dropdown */}
                        <div className="relative">
                            <NavDropdown
                                title={
                                    <div className="flex items-center gap-2 cursor-pointer bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 hover:shadow-md transition-all">
                                        <img src="/assets/img/team/team-4.jpg" alt="Profile" className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm" />
                                        <span className="hidden md:block text-sm font-medium text-gray-700">{currentUser.name}</span>
                                    </div>
                                }
                                id="profile-dropdown"
                                align="end"
                                className="custom-dropdown"
                            >
                                <NavDropdown.Item href="#settings" className="text-sm">Settings</NavDropdown.Item>
                                <NavDropdown.Item href="#profile" data-bs-toggle="offcanvas" data-bs-target="#profileSidebar" className="text-sm">Profile</NavDropdown.Item>
                                <NavDropdown.Divider />
                                <NavDropdown.Item onClick={handleLogOut} className="text-sm text-red-500 hover:bg-red-50">Logout</NavDropdown.Item>
                            </NavDropdown>
                        </div>
                    </div>
                </header>

                {/* Main Content Area */}
                <main className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
                    <div className="max-w-7xl mx-auto">
                        <Component />
                    </div>
                </main>

                <Profile />
            </div>
        </div>
    );
};

export default AdminRoute;